import React, { useEffect, useMemo, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconArrowUpRightOutline18 as ArrowUpRight,
	IconAtom2Outline18 as Atom,
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
	IconChevronRightOutline18 as ChevronRight,
	IconChevronUpOutline18 as ChevronUp,
	IconCopyOutline18 as Copy,
	IconDatabaseOutline18 as Database,
	IconLink2Outline18 as Link2,
	IconPencilOutline18 as Pencil,
	IconSquaresConnectedOutline18 as SquaresConnected,
	IconThumbsDownOutline18 as ThumbsDown,
	IconThumbsUpOutline18 as ThumbsUp,
	IconCircleXmarkOutline18 as XCircle,
} from "nucleo-ui-outline-18"

import type {
	AiChatFeedbackSentiment,
	AiChatMessage,
	AICiteItem,
	ConnectorAttribution,
} from "@productlane/lib/src/ai"
import { isCopilotFeedbackLongEnough } from "@productlane/lib/src/ai"
import { cn } from "@productlane/lib/src/cn"
import { htmlToPlainText } from "@productlane/lib/src/html/browser"

import { useCopiedFlag } from "../../hooks/useCopiedFlag"
import { useTextClamped } from "../../hooks/useTextClamped"
import { ImportantIcon } from "../../icons/important-icon"
import { IssueStatusIcon } from "../../icons/issue-status-icons"
import { IssueCompletedIcon } from "../../icons/issues/issue-completed-icon"
import { ClaudeIcon } from "../../icons/project/claude-icon"
import { Badge } from "../badge"
import { Button } from "../button"
import { Dialog } from "../dialog"
import { IconButton } from "../icon-button"
import { Popover } from "../popover"
import {
	AgentInstructionsProposalCard,
	LinearIssueProposalCard,
} from "./agent-setup-cards"
import { elapsedLabel } from "./docs-proposal-elapsed"

export const AiSuggestionButton = ({
	disabled,
	onClick,
	children,
	index = 0,
}: {
	disabled: boolean
	onClick: () => void
	children: React.ReactNode
	index?: number
}) => {
	return (
		<motion.div
			initial={{ opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.15, delay: 0.1 + index * 0.05 }}
		>
			<Button
				disabled={disabled}
				onClick={onClick}
				variant="outline"
				className="relative -ml-2 h-10 overflow-hidden truncate rounded-full bg-white/60 px-3.5 text-sm tracking-tight shadow-lg ring-black/10 backdrop-blur-md hover:!bg-white dark:bg-gray-800/60 dark:shadow-black/40 dark:hover:!bg-gray-700/30"
			>
				<span className="relative z-10 font-medium text-gray-800 dark:text-gray-200">
					{children}
				</span>
			</Button>
		</motion.div>
	)
}

export const LinearIssueBadge = ({
	icon: Icon,
	label,
}: {
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
	label: string
}) => {
	return (
		<span className="ml-auto flex items-center gap-1 text-gray-400 dark:text-gray-500">
			<Icon className="h-3 w-3" strokeWidth={1.75} />
			<span className="text-[11px]">{label}</span>
		</span>
	)
}

export const LinearIssueCard = ({
	type,
	id,
	identifier,
	title,
	description,
	outdated,
	important: initialImportant,
	messageCount,
	sendMessage,
	isProcessing,
	status: statusProp,
}: {
	type: "created" | "edited" | "removed" | "linked"
	id?: string
	identifier?: string
	title?: string
	description?: string
	outdated?: boolean
	important?: boolean
	messageCount: number
	sendMessage: (message: string) => void
	isProcessing: boolean
	status?: string
}) => {
	const [expanded, setExpanded] = useState(false)
	const [descriptionEl, setDescriptionEl] =
		useState<HTMLParagraphElement | null>(null)
	const descriptionClamped = useTextClamped(
		descriptionEl,
		!expanded,
		description,
	)
	const [important, setImportant] = useState(initialImportant ?? false)

	useEffect(() => {
		if (initialImportant !== undefined) {
			setImportant(initialImportant)
		}
	}, [initialImportant])

	useEffect(() => {
		setExpanded(false)
	}, [messageCount])

	const isRemoved = type === "removed"
	const status = isRemoved
		? "canceled"
		: type === "linked" && statusProp
			? statusProp
			: "triage"

	const handleToggleImportance = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!identifier || isProcessing || outdated) return
		const message = important
			? `Unmark ${title} (${identifier}) as important`
			: `Mark ${title} (${identifier}) as important`
		setImportant(!important)
		sendMessage(message)
	}

	return (
		<motion.div
			initial={{ opacity: 0, y: 8 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.2 }}
			className={cn(
				"group/card relative -mx-2 mb-2 mt-4 cursor-default overflow-hidden rounded-lg border border-gray-200 bg-white p-2 text-sm dark:border-gray-800 dark:bg-gray-900",
				outdated &&
					"pointer-events-none border-gray-100 opacity-60 dark:border-gray-800/60 dark:opacity-60",
			)}
		>
			<div className="flex items-center gap-2 pl-1">
				<IssueStatusIcon status={status} className="h-3.5 w-3.5 flex-none" />
				{identifier && (
					<span className="tabular-nums text-secondary">{identifier}</span>
				)}
				{outdated && (
					<span className="ml-auto text-[10px] font-medium tracking-wide text-gray-400 dark:text-gray-500">
						Outdated
					</span>
				)}
			</div>
			{title && (
				<div className="mt-1 truncate pl-1 font-medium text-primary">
					{title}
				</div>
			)}

			{id && !isRemoved && !outdated && (
				<div className="absolute right-[3px] top-[6px] flex items-center px-1">
					<div className="pl-1">
						<button
							onClick={handleToggleImportance}
							disabled={isProcessing}
							className={cn(
								"rounded-md p-1 transition-colors dark:hover:bg-gray-800",
								important
									? "!text-red-400 hover:bg-gray-200 hover:!text-red-400"
									: "text-gray-300 hover:bg-gray-200 hover:text-black dark:hover:text-white",
								isProcessing && "opacity-50",
							)}
						>
							<ImportantIcon className="h-[0.8rem] w-[0.8rem]" />
						</button>
					</div>
				</div>
			)}

			{description && !isRemoved && !outdated && (
				<div className="mt-1.5 border-t border-gray-200 pt-1.5 dark:border-gray-800">
					<p
						ref={setDescriptionEl}
						className={cn(
							"pl-1 text-xs leading-relaxed text-secondary",
							!expanded && "line-clamp-3",
						)}
					>
						{description}
					</p>
					{descriptionClamped && (
						<button
							onClick={() => setExpanded(!expanded)}
							className="mt-1 flex items-center gap-1 pl-1 text-[11px] text-secondary transition-colors hover:text-primary"
						>
							{expanded ? (
								<ChevronUp className="h-3 w-3 flex-none" />
							) : (
								<ChevronDown className="h-3 w-3 flex-none" />
							)}
							{expanded ? "Show less" : "Show more"}
						</button>
					)}
				</div>
			)}
		</motion.div>
	)
}

export const AiChatFeedbackModal = ({
	isOpen,
	onOpenChange,
	onSubmit,
}: {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	onSubmit: (text: string) => void
}) => {
	const [feedbackText, setFeedbackText] = useState("")
	const [isSending, setIsSending] = useState(false)
	const isLongEnough = isCopilotFeedbackLongEnough(feedbackText)

	const handleSubmit = () => {
		if (!isLongEnough) return
		setIsSending(true)
		onSubmit(feedbackText.trim())
		setFeedbackText("")
		setIsSending(false)
		onOpenChange(false)
	}

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title="Leave feedback"
			description="Send our team your feedback for the AI generated content"
			showCloseButton={false}
			footer={
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						className="rounded-lg"
					>
						Cancel
					</Button>
					<Button
						disabled={!isLongEnough || isSending}
						onClick={handleSubmit}
						className="rounded-lg"
					>
						Send
					</Button>
				</div>
			}
		>
			<textarea
				value={feedbackText}
				onChange={(e) => setFeedbackText(e.target.value)}
				placeholder="What was wrong with this answer?"
				data-testid="ai-chat-feedback-input"
				className="min-h-[120px] w-full resize-none rounded-lg border border-gray-200 bg-transparent p-3 text-sm text-primary outline-none placeholder:text-gray-400 focus:border-gray-300 dark:border-gray-700 dark:placeholder:text-gray-500 dark:focus:border-gray-600"
			/>
			{!isLongEnough && feedbackText.trim().length > 0 && (
				<p className="mt-1.5 text-xs text-secondary">
					Add a bit more, at least a sentence on what went wrong.
				</p>
			)}
		</Dialog>
	)
}

export interface AiChatFeedbackInput {
	sentiment: AiChatFeedbackSentiment
	comment?: string
}

export const AiChatMessageActions = ({
	message,
	sentiment,
	onFeedback,
	onCopy,
	alwaysVisible = false,
	showIssueBadges = true,
}: {
	message: AiChatMessage
	/** The rating already stored on this message. */
	sentiment?: AiChatFeedbackSentiment | null
	onFeedback?: (input: AiChatFeedbackInput) => void
	onCopy?: () => void
	/** Keeps the row on screen instead of revealing it on hover. */
	alwaysVisible?: boolean
	/** Off where the linked issue already renders as its own card. */
	showIssueBadges?: boolean
}) => {
	const [copied, markCopied] = useCopiedFlag(2000)
	const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
	const [optimisticSentiment, setOptimisticSentiment] = useState<
		AiChatFeedbackSentiment | null | undefined
	>(undefined)
	const rating =
		optimisticSentiment !== undefined
			? optimisticSentiment
			: (sentiment ?? null)

	const handleThumbsUp = () => {
		if (rating === "positive") return
		setOptimisticSentiment("positive")
		onFeedback?.({ sentiment: "positive" })
	}

	const handleFeedbackSubmit = (text: string) => {
		setOptimisticSentiment("negative")
		onFeedback?.({ sentiment: "negative", comment: text })
	}

	const handleCopy = async () => {
		const textContent = htmlToPlainText(message.content)
		try {
			await navigator.clipboard.writeText(textContent)
			markCopied()
			onCopy?.()
		} catch {
			console.error("Failed to copy to clipboard")
		}
	}

	return (
		<>
			<div
				data-testid="ai-chat-message-actions"
				className={cn(
					"flex items-center pt-1",
					!alwaysVisible &&
						"opacity-0 transition-opacity focus-within:opacity-100 group-hover/message:opacity-100",
				)}
			>
				<div className="-ml-0.5 flex items-center gap-0.5">
					{onFeedback && (
						<>
							<IconButton
								Icon={ThumbsUp}
								iconStrokeWidth={rating === "positive" ? 2 : 1.75}
								variant="ghost"
								size="sm"
								aria-label="Good answer"
								aria-pressed={rating === "positive"}
								data-testid="ai-chat-thumbs-up"
								onClick={handleThumbsUp}
								className={cn(
									rating === "positive"
										? "text-primary"
										: "text-gray-400 hover:text-primary dark:text-gray-500",
								)}
								iconClassName="h-3.5 w-3.5"
							/>
							<IconButton
								Icon={ThumbsDown}
								iconStrokeWidth={rating === "negative" ? 2 : 1.75}
								variant="ghost"
								size="sm"
								aria-label="Bad answer"
								aria-pressed={rating === "negative"}
								data-testid="ai-chat-thumbs-down"
								onClick={() => setFeedbackModalOpen(true)}
								className={cn(
									rating === "negative"
										? "text-primary"
										: "text-gray-400 hover:text-primary dark:text-gray-500",
								)}
								iconClassName="h-3.5 w-3.5"
							/>
						</>
					)}
					<IconButton
						Icon={copied ? Check : Copy}
						iconStrokeWidth={copied ? 2 : 1.75}
						variant="ghost"
						size="sm"
						aria-label="Copy answer"
						data-testid="ai-chat-copy-answer"
						onClick={handleCopy}
						className="text-gray-400 hover:text-primary dark:text-gray-500"
						iconClassName="h-3.5 w-3.5"
					/>
				</div>
				{showIssueBadges && (
					<>
						{message.linearIssueCreated && (
							<LinearIssueBadge
								icon={IssueCompletedIcon}
								label="Issue created"
							/>
						)}
						{message.linearIssueLinked && (
							<LinearIssueBadge icon={Link2} label="Issue linked" />
						)}
						{message.linearIssueEdited && (
							<LinearIssueBadge icon={Pencil} label="Issue edited" />
						)}
						{message.linearIssueRemoved && (
							<LinearIssueBadge icon={XCircle} label="Issue removed" />
						)}
					</>
				)}
			</div>
			{onFeedback && (
				<AiChatFeedbackModal
					isOpen={feedbackModalOpen}
					onOpenChange={setFeedbackModalOpen}
					onSubmit={handleFeedbackSubmit}
				/>
			)}
		</>
	)
}

export interface DocsProposalChange {
	id: string
	kind: "CREATE" | "EDIT" | "DELETE"
	title: string
	route: string
}

export type DocsProposalStatus =
	| "pending"
	| "generating"
	| "ready"
	| "skipped"
	| "error"

export interface DocsProposalStatusRowProps {
	summary: string
	issueIdentifiers?: string[]
	// Left out for a run scoped to a thread, which has no issue chips to show.
	issueCount?: number
	prUrls?: string[]
	status: DocsProposalStatus
	changes?: DocsProposalChange[]
	rationale?: string
	startedAt?: Date
	finishedAt?: number
	errorMessage?: string
}

const DOCS_CARD_EASE = [0.23, 1, 0.32, 1] as const
const DOCS_CARD_ENTER = { duration: 0.28, ease: DOCS_CARD_EASE }
const DOCS_CARD_SWAP = { duration: 0.16, ease: DOCS_CARD_EASE }

const IssueChip = ({ children }: { children: React.ReactNode }) => (
	<span className="rounded-full bg-gray-100 px-1.5 py-[1px] font-mono text-[10px] tabular-nums text-secondary ring-1 ring-inset ring-gray-200 dark:bg-gray-800/60 dark:text-gray-300 dark:ring-gray-700/60">
		{children}
	</span>
)

function getPrNumberFromUrl(url: string): number | null {
	const match = /\/pull\/(\d+)(?:\/|$)/.exec(url)
	if (!match) return null
	const n = Number(match[1])
	return Number.isFinite(n) ? n : null
}

const CHANGE_KIND_ACTION: Record<DocsProposalChange["kind"], string> = {
	CREATE: "Wrote",
	EDIT: "Edited",
	DELETE: "Marked for deletion",
}

// The two shapes the trail alternates between: what the agent was thinking,
// quoted, and what it did, stated plainly.
const DocsProposalThought = ({ children }: { children: React.ReactNode }) => (
	<span className="border-l-2 border-gray-200 pl-2.5 text-[12.5px] leading-relaxed text-secondary dark:border-gray-700/80">
		{children}
	</span>
)

const DocsProposalAction = ({ children }: { children: React.ReactNode }) => (
	<span className="text-[12.5px] font-medium text-primary">{children}</span>
)

const DOCS_HEADLINE: Record<DocsProposalStatus, string> = {
	pending: "Docs update ready to draft",
	generating: "Drafting docs update",
	ready: "Drafted docs update",
	skipped: "Read the docs, changed nothing",
	error: "Could not start the docs update",
}

// Ticks only while the run is open, so a resolved row renders once and stops.
function useElapsedLabel(
	startedAt: Date | undefined,
	finishedAt: number | undefined,
	isRunning: boolean,
): string | null {
	const [now, setNow] = useState(() => Date.now())

	useEffect(() => {
		if (!isRunning) return
		const id = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(id)
	}, [isRunning])

	return elapsedLabel(startedAt, finishedAt ?? now)
}

export const DocsProposalStatusRow = ({
	summary,
	issueIdentifiers,
	issueCount,
	prUrls,
	status,
	changes,
	rationale,
	startedAt,
	finishedAt,
	errorMessage,
}: DocsProposalStatusRowProps) => {
	const reduce = useReducedMotion()
	const [expanded, setExpanded] = useState(false)

	const isRunning = status === "generating"
	const elapsed = useElapsedLabel(startedAt, finishedAt, isRunning)

	const visibleIssues = issueIdentifiers?.slice(0, 3) ?? []
	const overflowCount = (issueIdentifiers?.length ?? 0) - visibleIssues.length
	const hasSources =
		visibleIssues.length > 0 || issueCount === 0 || (prUrls?.length ?? 0) > 0

	return (
		<motion.div
			initial={reduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
			animate={{ opacity: 1, y: 0 }}
			transition={DOCS_CARD_SWAP}
			className="mb-1.5 flex flex-col items-start gap-1.5"
		>
			<button
				type="button"
				onClick={() => setExpanded((v) => !v)}
				className="-mx-1 flex max-w-full cursor-default items-center gap-1 rounded-md px-1 py-0.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
			>
				<Atom
					strokeWidth={2}
					className={`-ml-0.5 h-3.5 w-3.5 flex-none text-secondary ${
						isRunning ? "animate-pulse" : ""
					}`}
				/>
				<span
					className={`truncate text-[12.5px] font-medium text-secondary ${
						isRunning ? "animate-pulse" : ""
					}`}
				>
					{DOCS_HEADLINE[status]}
				</span>
				{elapsed && (
					<span className="flex-none text-[11.5px] tabular-nums text-tertiary">
						· {elapsed}
					</span>
				)}
				<ChevronRight
					strokeWidth={2}
					className={`h-3 w-3 flex-none text-tertiary transition-transform duration-150 ease-out ${
						expanded ? "rotate-90" : ""
					}`}
				/>
			</button>

			<AnimatePresence initial={false}>
				{expanded && (
					<motion.div
						key="docs-detail"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={DOCS_CARD_SWAP}
						className="flex flex-col gap-2 pl-[3px]"
					>
						<DocsProposalThought>{summary}</DocsProposalThought>
						{isRunning && (
							<DocsProposalAction>
								<span className="animate-pulse">Reading the help center</span>
							</DocsProposalAction>
						)}
						{(status === "ready" || status === "skipped") && (
							<DocsProposalAction>Read the help center</DocsProposalAction>
						)}
						{rationale && (
							<DocsProposalThought>{rationale}</DocsProposalThought>
						)}
						{changes?.map((change) => (
							<DocsProposalAction key={change.id}>
								{CHANGE_KIND_ACTION[change.kind]} {change.title}
							</DocsProposalAction>
						))}
						{status === "skipped" && (
							<DocsProposalAction>
								Left every article as it was
							</DocsProposalAction>
						)}
						{status === "error" && errorMessage && (
							<span className="text-[12.5px] text-red-500 dark:text-red-400">
								{errorMessage}
							</span>
						)}
						{hasSources && (
							<div className="flex flex-wrap items-center gap-1 pt-0.5">
								{issueCount === 0 && (prUrls?.length ?? 0) === 0 ? (
									<IssueChip>workspace</IssueChip>
								) : (
									<>
										{visibleIssues.map((id) => (
											<IssueChip key={id}>{id}</IssueChip>
										))}
										{overflowCount > 0 && (
											<IssueChip>+{overflowCount}</IssueChip>
										)}
									</>
								)}
								{prUrls?.map((url) => {
									const n = getPrNumberFromUrl(url)
									return (
										<a
											key={url}
											href={url}
											target="_blank"
											rel="noreferrer"
											className="cursor-default rounded-full bg-gray-100 px-1.5 py-[1px] font-mono text-[10px] tabular-nums text-secondary ring-1 ring-inset ring-gray-200 hover:bg-gray-200 dark:bg-gray-800/60 dark:text-gray-300 dark:ring-gray-700/60 dark:hover:bg-gray-700/60"
										>
											{n !== null ? `PR #${n}` : "PR"}
										</a>
									)
								})}
							</div>
						)}
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	)
}

// ─── Continue in Claude Card ────────────────────────────────────────────────

export const ContinueInClaudeCard = ({
	summary,
	onContinue,
	disabled = false,
}: {
	summary: string
	onContinue: () => void
	disabled?: boolean
}) => {
	const reduce = useReducedMotion()
	const initial = reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }
	const animate = reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }

	return (
		<motion.div
			initial={initial}
			animate={animate}
			transition={DOCS_CARD_ENTER}
			className="my-3 flex flex-col gap-3 overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50/60 p-3.5 shadow-[0_1px_0_rgba(0,0,0,0.02),0_2px_6px_-2px_rgba(0,0,0,0.04)] ring-1 ring-inset ring-white/60 transition-colors duration-200 ease-out hover:border-gray-300 dark:border-gray-800 dark:from-gray-900 dark:to-gray-950/40 dark:ring-white/[0.02] dark:hover:border-gray-700"
		>
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<div className="flex items-center gap-1.5">
					<ClaudeIcon className="text-[#D97757]" />
					<span className="text-xs font-medium text-primary">
						Continue in Claude
					</span>
				</div>
				<div className="text-[12.5px] leading-snug text-secondary">
					{summary}
				</div>
			</div>

			<div className="-mx-3.5 -mb-3.5 flex items-center justify-end border-t border-gray-200/70 bg-gray-50/50 px-3.5 py-2.5 dark:border-gray-800/70 dark:bg-gray-900/40">
				<Button
					variant="outline"
					size="sm"
					className="h-7 gap-1.5 rounded-md text-xs font-medium"
					disabled={disabled}
					onClick={onContinue}
				>
					Open in Claude Code
					<ArrowUpRight strokeWidth={2} className="h-3.5 w-3.5" />
				</Button>
			</div>
		</motion.div>
	)
}

// ─── Stripe Action Approval Card ────────────────────────────────────────────

const STRIPE_ACTION_LABEL: Record<
	NonNullable<AiChatMessage["stripeActionApproval"]>["actionType"],
	string
> = {
	apply_promotion_code: "Apply promotion code",
	remove_promotion_code: "Remove promotion code",
	cancel_subscription: "Cancel subscription",
	change_plan: "Change plan",
	extend_trial: "Extend trial",
}

function getStripeWorkspaceName(
	preview: Record<string, unknown>,
): string | null {
	const workspace = preview.workspace
	if (workspace && typeof workspace === "object" && "name" in workspace) {
		const name = (workspace as { name?: unknown }).name
		if (typeof name === "string") return name
	}
	return null
}

interface StripePreviewRow {
	label: string
	before?: string
	after: string
}

function formatDiscount(
	discount: { code?: string; description?: string } | null,
): string {
	if (!discount) return "None"
	const code = discount.code?.trim()
	const description = discount.description?.trim()
	if (code && description) return `${code} · ${description}`
	return code ?? description ?? "Discount"
}

// Stripe sends ISO timestamps; agent idea examples send free text like "Nov 3".
function formatStripeDate(value: string): string {
	return /^\d{4}-\d{2}-\d{2}T/.test(value) ? value.slice(0, 10) : value
}

export function getStripePreviewRows(
	actionType: NonNullable<AiChatMessage["stripeActionApproval"]>["actionType"],
	preview: Record<string, unknown>,
): StripePreviewRow[] {
	const before = (preview.before ?? {}) as Record<string, unknown>
	const after = (preview.after ?? {}) as Record<string, unknown>

	if (
		actionType === "apply_promotion_code" ||
		actionType === "remove_promotion_code"
	) {
		const beforeDiscount = (before.discount ?? null) as {
			code?: string
			description?: string
		} | null
		const afterDiscount = (after.discount ?? null) as {
			code?: string
			description?: string
		} | null
		return [
			{
				label: "Discount",
				before: formatDiscount(beforeDiscount),
				after: formatDiscount(afterDiscount),
			},
		]
	}

	if (actionType === "cancel_subscription") {
		const periodEnd =
			typeof after.periodEnd === "string" ? after.periodEnd : null
		return periodEnd
			? [{ label: "Effective", after: formatStripeDate(periodEnd) }]
			: []
	}

	if (actionType === "change_plan") {
		const beforePrice = (before.price ?? null) as {
			nickname?: string | null
			id?: string | null
			amount?: string | null
		} | null
		const afterPrice = (after.price ?? null) as {
			nickname?: string | null
			id?: string | null
			amount?: string | null
		} | null
		const rows: StripePreviewRow[] = [
			{
				label: "Plan",
				before: beforePrice?.nickname ?? beforePrice?.id ?? "—",
				after: afterPrice?.nickname ?? afterPrice?.id ?? "—",
			},
		]
		if (beforePrice?.amount || afterPrice?.amount) {
			rows.push({
				label: "Price",
				before: beforePrice?.amount ?? "—",
				after: afterPrice?.amount ?? "—",
			})
		}
		return rows
	}

	if (actionType === "extend_trial") {
		const beforeTrialEnd =
			typeof before.trialEnd === "string" ? before.trialEnd : null
		const afterTrialEnd =
			typeof after.trialEnd === "string" ? after.trialEnd : null
		return [
			{
				label: "Trial ends",
				before: beforeTrialEnd ? formatStripeDate(beforeTrialEnd) : "None",
				after: afterTrialEnd ? formatStripeDate(afterTrialEnd) : "-",
			},
		]
	}

	return []
}

export interface StripeActionApprovalCardProps {
	actionType: NonNullable<AiChatMessage["stripeActionApproval"]>["actionType"]
	summary: string
	preview: Record<string, unknown>
	status: NonNullable<AiChatMessage["stripeActionApproval"]>["status"]
	subscriptionUrl?: string
	errorMessage?: string
	disabled?: boolean
	// An example of what an action would look like, with no decision to make.
	exampleOnly?: boolean
	onApprove: () => void
	onReject: () => void
}

export const StripeActionApprovalCard = ({
	actionType,
	summary,
	preview,
	status,
	subscriptionUrl,
	errorMessage,
	disabled = false,
	exampleOnly = false,
	onApprove,
	onReject,
}: StripeActionApprovalCardProps) => {
	const reduce = useReducedMotion()
	const workspaceName = getStripeWorkspaceName(preview)
	const rows = getStripePreviewRows(actionType, preview)
	const reason = typeof preview.reason === "string" ? preview.reason : null

	const initial = reduce ? { opacity: 0 } : { opacity: 0, y: 6 }
	const animate = reduce ? { opacity: 1 } : { opacity: 1, y: 0 }

	const isPending = status === "pending"
	const isExecuting = status === "executing"
	const isExecuted = status === "executed"
	const isRejected = status === "rejected"
	const isErrored = status === "errored"

	const summaryHasWorkspace =
		!!workspaceName &&
		summary.toLowerCase().includes(workspaceName.toLowerCase())

	return (
		<motion.div
			initial={initial}
			animate={animate}
			transition={DOCS_CARD_ENTER}
			className="my-3 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
		>
			<div className="flex flex-col gap-1.5 px-4 pb-3 pt-3.5">
				<span className="text-xs font-medium text-tertiary">
					{STRIPE_ACTION_LABEL[actionType]}
				</span>
				<p className="text-[13px] leading-snug text-primary">{summary}</p>
				{!summaryHasWorkspace && workspaceName && (
					<span className="text-[11px] text-tertiary">{workspaceName}</span>
				)}
			</div>

			{rows.length > 0 && (
				<div className="flex flex-col gap-1.5 border-t border-gray-100 px-4 py-3 dark:border-gray-800/70">
					{rows.map((row) => (
						<div
							key={row.label}
							className="grid grid-cols-[72px_1fr] items-center gap-3 text-xs"
						>
							<span className="text-tertiary">{row.label}</span>
							<div className="flex min-w-0 items-center gap-1.5">
								{row.before !== undefined && (
									<>
										<span className="truncate text-tertiary">{row.before}</span>
										<ArrowRight
											strokeWidth={1.75}
											className="h-3 w-3 flex-none text-tertiary opacity-60"
										/>
									</>
								)}
								<span className="truncate font-medium tabular-nums text-primary">
									{row.after}
								</span>
							</div>
						</div>
					))}
				</div>
			)}

			{reason && (
				<p className="border-t border-gray-100 px-4 py-3 text-[11px] leading-relaxed text-tertiary dark:border-gray-800/70">
					{reason}
				</p>
			)}

			{isErrored && errorMessage && (
				<p className="border-t border-gray-100 px-4 py-3 text-[11px] leading-relaxed text-red-500 dark:border-gray-800/70 dark:text-red-400">
					{errorMessage}
				</p>
			)}

			<div
				className={cn(
					"flex items-center justify-end gap-2 border-t border-gray-200/70 bg-gray-50/50 px-4 py-2.5 dark:border-gray-800/70 dark:bg-gray-900/40",
					exampleOnly && "hidden",
				)}
			>
				<AnimatePresence mode="wait" initial={false}>
					{isPending && (
						<motion.div
							key="cta-pending"
							initial={{ opacity: 0, filter: "blur(2px)" }}
							animate={{ opacity: 1, filter: "blur(0px)" }}
							exit={{ opacity: 0, filter: "blur(2px)" }}
							transition={DOCS_CARD_SWAP}
							className="flex items-center gap-2"
						>
							<Button
								variant="outline"
								size="sm"
								className="h-7 gap-1.5 rounded-md text-xs font-medium"
								disabled={disabled}
								onClick={onReject}
							>
								Reject
							</Button>
							<Button
								size="sm"
								className="h-7 gap-1.5 rounded-md text-xs font-medium"
								disabled={disabled}
								onClick={onApprove}
							>
								Approve
							</Button>
						</motion.div>
					)}
					{isExecuting && (
						<motion.div
							key="cta-executing"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={DOCS_CARD_SWAP}
							className="pr-1 text-[11px] text-tertiary"
						>
							<span className="animate-pulse">Executing…</span>
						</motion.div>
					)}
					{isExecuted && (
						<motion.div
							key="cta-executed"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={DOCS_CARD_SWAP}
							className="flex items-center gap-2 pr-1 text-[11px]"
						>
							<span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
								<Check strokeWidth={2.25} className="h-3.5 w-3.5" />
								Applied
							</span>
							{subscriptionUrl && (
								<a
									href={subscriptionUrl}
									target="_blank"
									rel="noreferrer"
									className="flex items-center gap-0.5 rounded-md text-tertiary transition-colors hover:text-secondary"
								>
									Verify in Stripe
									<ArrowUpRight strokeWidth={2} className="h-3.5 w-3.5" />
								</a>
							)}
						</motion.div>
					)}
					{isRejected && (
						<motion.div
							key="cta-rejected"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={DOCS_CARD_SWAP}
							className="flex items-center gap-1 pr-1 text-[11px] text-tertiary"
						>
							<XCircle strokeWidth={2} className="h-3.5 w-3.5" />
							<span>Rejected</span>
						</motion.div>
					)}
					{isErrored && (
						<motion.div
							key="cta-errored"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={DOCS_CARD_SWAP}
						>
							<Button
								variant="outline"
								size="sm"
								className="h-7 gap-1.5 rounded-md text-xs font-medium"
								disabled={disabled}
								onClick={onApprove}
							>
								Try again
							</Button>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</motion.div>
	)
}

// ─── Postgres Action Approval Card ──────────────────────────────────────────

const POSTGRES_CARD_EASE = [0.16, 1, 0.3, 1] as const
const POSTGRES_CARD_ENTER = { duration: 0.32, ease: POSTGRES_CARD_EASE }
const POSTGRES_CARD_SWAP = { duration: 0.16, ease: POSTGRES_CARD_EASE }

const POSTGRES_PARAM_TINT: Record<string, string> = {
	string: "text-blue-600 dark:text-blue-400",
	number: "text-amber-600 dark:text-amber-400",
	boolean: "text-emerald-600 dark:text-emerald-400",
	timestamp: "text-violet-600 dark:text-violet-400",
}

// Lightweight SQL keyword highlighter. Not a parser. The keyword list is the
// common-case set; everything else stays as plain text. Renders inline so it
// still wraps and selects cleanly inside the <pre>.
function highlightSql(sql: string): React.ReactNode[] {
	const KEYWORDS = new Set([
		"select",
		"from",
		"where",
		"and",
		"or",
		"not",
		"in",
		"is",
		"null",
		"order",
		"by",
		"group",
		"having",
		"limit",
		"offset",
		"join",
		"left",
		"right",
		"inner",
		"outer",
		"full",
		"on",
		"as",
		"distinct",
		"case",
		"when",
		"then",
		"else",
		"end",
		"with",
		"union",
		"all",
		"between",
		"like",
		"ilike",
		"asc",
		"desc",
	])
	const tokens = sql.split(/(\s+|,|\(|\)|;)/)
	return tokens.map((tok, i) => {
		if (!tok) return null
		const lower = tok.toLowerCase()
		if (KEYWORDS.has(lower)) {
			return (
				<span
					key={i}
					className="font-medium text-violet-600 dark:text-violet-400"
				>
					{tok}
				</span>
			)
		}
		if (/^'[^']*'$/.test(tok) || /^\d+$/.test(tok)) {
			return (
				<span key={i} className="text-amber-600 dark:text-amber-400">
					{tok}
				</span>
			)
		}
		if (/^\$\d+$/.test(tok)) {
			return (
				<span key={i} className="text-blue-600 dark:text-blue-400">
					{tok}
				</span>
			)
		}
		return <span key={i}>{tok}</span>
	})
}

interface WhitelistSuggestion {
	name: string
	description: string
	sql: string
	parameters: { name: string; type: string }[]
}

export interface PostgresActionApprovalCardProps {
	sql: string
	reason?: string | null
	summary: string
	status: NonNullable<AiChatMessage["postgresActionApproval"]>["status"]
	whitelistSuggestion?: WhitelistSuggestion | null
	result?: NonNullable<AiChatMessage["postgresActionApproval"]>["result"]
	savedWhitelistName?: string | null
	errorMessage?: string
	disabled?: boolean
	onApprove: (opts: { saveWhitelist: boolean }) => void
	onReject: () => void
}

export const PostgresActionApprovalCard = ({
	sql,
	reason,
	summary,
	status,
	whitelistSuggestion,
	result,
	savedWhitelistName,
	errorMessage,
	disabled = false,
	onApprove,
	onReject,
}: PostgresActionApprovalCardProps) => {
	const reduce = useReducedMotion()
	const [showRows, setShowRows] = useState(false)
	const initial = reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.985 }
	const animate = reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }

	const isPending = status === "pending"
	const isExecuting = status === "executing"
	const isExecuted = status === "executed"
	const isRejected = status === "rejected"
	const isErrored = status === "errored"

	const hasSuggestion =
		!!whitelistSuggestion && whitelistSuggestion.parameters !== undefined

	return (
		<motion.div
			initial={initial}
			animate={animate}
			transition={POSTGRES_CARD_ENTER}
			className="my-3 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:border-gray-800 dark:bg-gray-900"
			style={{ willChange: "transform" }}
		>
			{/* Header */}
			<motion.div
				initial={reduce ? false : { opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.28, delay: 0.04, ease: POSTGRES_CARD_EASE }}
				className="flex flex-col gap-1.5 px-4 pb-3 pt-3.5"
			>
				<span className="flex items-center gap-1.5 text-[10px] font-medium tracking-[0.08em] text-tertiary">
					<Database strokeWidth={1.75} className="h-3.5 w-3.5" />
					Database query
				</span>
				<p className="text-[13px] leading-snug text-primary">{summary}</p>
			</motion.div>

			{/* SQL preview */}
			<motion.pre
				initial={reduce ? false : { opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.28, delay: 0.08, ease: POSTGRES_CARD_EASE }}
				className="overflow-x-auto border-t border-gray-100 bg-gray-50/60 px-4 py-3 font-mono text-[11.5px] leading-relaxed text-secondary dark:border-gray-800/70 dark:bg-gray-950/40"
			>
				<code className="whitespace-pre-wrap break-words">
					{highlightSql(sql)}
				</code>
			</motion.pre>

			{reason && (
				<p className="border-t border-gray-100 px-4 py-2.5 text-[11px] leading-relaxed text-tertiary dark:border-gray-800/70">
					{reason}
				</p>
			)}

			{/* Whitelist suggestion: shown only while pending. After approval the
			    confirmed name moves to the result panel. */}
			{isPending && hasSuggestion && whitelistSuggestion && (
				<motion.div
					initial={reduce ? false : { opacity: 0, y: 4 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.28, delay: 0.12, ease: POSTGRES_CARD_EASE }}
					className="flex flex-col gap-1.5 border-t border-gray-100 bg-gradient-to-b from-violet-50/40 to-transparent px-4 py-3 dark:border-gray-800/70 dark:from-violet-900/10"
				>
					<div className="flex items-center gap-1.5 text-[10px] font-medium tracking-[0.08em] text-violet-600 dark:text-violet-400">
						<Atom strokeWidth={2} className="h-3.5 w-3.5" />
						Reusable
					</div>
					<div className="flex items-baseline gap-2">
						<span className="rounded bg-white px-1.5 py-0.5 font-mono text-[12px] font-medium tabular-nums text-primary shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)] dark:bg-gray-800/80 dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]">
							{whitelistSuggestion.name}
						</span>
						{whitelistSuggestion.parameters.length > 0 && (
							<span className="font-mono text-[10.5px] tracking-tight text-tertiary">
								(
								{whitelistSuggestion.parameters.map((p, i) => (
									<React.Fragment key={p.name}>
										{i > 0 && ", "}
										<span className={POSTGRES_PARAM_TINT[p.type] ?? ""}>
											{p.name}
										</span>
									</React.Fragment>
								))}
								)
							</span>
						)}
					</div>
					<p className="text-[11.5px] leading-relaxed text-secondary">
						{whitelistSuggestion.description}
					</p>
				</motion.div>
			)}

			{/* Result panel (executed) */}
			<AnimatePresence initial={false}>
				{isExecuted && result && (
					<motion.div
						key="result"
						initial={reduce ? false : { opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						transition={POSTGRES_CARD_ENTER}
						className="overflow-hidden border-t border-gray-100 dark:border-gray-800/70"
					>
						<div className="flex items-center justify-between px-4 py-2.5">
							<div className="flex items-baseline gap-2 text-[11.5px] text-secondary">
								<span className="font-medium tabular-nums text-primary">
									{result.rowCount === 0
										? "No rows"
										: `${result.rowCount} row${result.rowCount === 1 ? "" : "s"}`}
								</span>
								{result.truncated && (
									<span className="text-[10.5px] tracking-[0.04em] text-amber-600 dark:text-amber-400">
										truncated
									</span>
								)}
								<span className="tabular-nums text-tertiary">
									{result.durationMs}&thinsp;ms
								</span>
							</div>
							{result.rowCount > 0 && (
								<button
									type="button"
									onClick={() => setShowRows((s) => !s)}
									className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-medium text-tertiary transition-[color,background-color,transform] duration-150 ease-out hover:bg-gray-100 hover:text-secondary dark:hover:bg-gray-800"
								>
									{showRows ? (
										<>
											<ChevronUp strokeWidth={2} className="h-3 w-3" />
											Hide rows
										</>
									) : (
										<>
											<ChevronDown strokeWidth={2} className="h-3 w-3" />
											Peek rows
										</>
									)}
								</button>
							)}
						</div>
						<AnimatePresence initial={false}>
							{showRows && result.rowCount > 0 && (
								<motion.div
									initial={{ opacity: 0, height: 0 }}
									animate={{ opacity: 1, height: "auto" }}
									exit={{ opacity: 0, height: 0 }}
									transition={POSTGRES_CARD_SWAP}
									className="overflow-hidden"
								>
									<div className="overflow-x-auto border-t border-gray-100 bg-gray-50/40 dark:border-gray-800/70 dark:bg-gray-950/30">
										<table className="w-full font-mono text-[10.5px]">
											<thead>
												<tr className="text-tertiary">
													{result.columns.map((c) => (
														<th
															key={c}
															className="border-b border-gray-100 px-3 py-1.5 text-left font-medium dark:border-gray-800/70"
														>
															{c}
														</th>
													))}
												</tr>
											</thead>
											<tbody>
												{result.rows.slice(0, 10).map((row, i) => (
													<tr key={i} className="text-secondary">
														{result.columns.map((c) => (
															<td
																key={c}
																className="max-w-[180px] truncate px-3 py-1 align-top"
																title={String(row[c] ?? "")}
															>
																{row[c] === null ? (
																	<span className="text-tertiary">null</span>
																) : typeof row[c] === "object" ? (
																	JSON.stringify(row[c])
																) : (
																	String(row[c])
																)}
															</td>
														))}
													</tr>
												))}
											</tbody>
										</table>
										{result.rows.length > 10 && (
											<div className="border-t border-gray-100 px-3 py-1.5 text-[10px] text-tertiary dark:border-gray-800/70">
												+{result.rows.length - 10} more row
												{result.rows.length - 10 === 1 ? "" : "s"} hidden
											</div>
										)}
									</div>
								</motion.div>
							)}
						</AnimatePresence>
						{savedWhitelistName && (
							<p className="border-t border-gray-100 px-4 py-2 text-[11px] text-violet-700 dark:border-gray-800/70 dark:text-violet-300">
								Saved as{" "}
								<span className="font-mono font-medium">
									{savedWhitelistName}
								</span>{" "}
								for future runs.
							</p>
						)}
					</motion.div>
				)}
			</AnimatePresence>

			{isErrored && errorMessage && (
				<p className="border-t border-gray-100 px-4 py-3 text-[11px] leading-relaxed text-red-500 dark:border-gray-800/70 dark:text-red-400">
					{errorMessage}
				</p>
			)}

			{/* Actions */}
			<div className="flex items-center justify-end gap-2 border-t border-gray-200/70 bg-gray-50/50 px-4 py-2.5 dark:border-gray-800/70 dark:bg-gray-900/40">
				<AnimatePresence mode="wait" initial={false}>
					{isPending && (
						<motion.div
							key="cta-pending"
							initial={{ opacity: 0, filter: "blur(2px)" }}
							animate={{ opacity: 1, filter: "blur(0px)" }}
							exit={{ opacity: 0, filter: "blur(2px)" }}
							transition={POSTGRES_CARD_SWAP}
							className="flex items-center gap-2"
						>
							<Button
								variant="outline"
								size="sm"
								className="h-7 gap-1.5 rounded-md text-xs font-medium"
								disabled={disabled}
								onClick={onReject}
							>
								Reject
							</Button>
							{hasSuggestion && (
								<Button
									variant="outline"
									size="sm"
									className="h-7 gap-1.5 rounded-md text-xs font-medium"
									disabled={disabled}
									onClick={() => onApprove({ saveWhitelist: false })}
								>
									Run once
								</Button>
							)}
							<Button
								size="sm"
								className="h-7 gap-1.5 rounded-md text-xs font-medium"
								disabled={disabled}
								onClick={() => onApprove({ saveWhitelist: hasSuggestion })}
							>
								{hasSuggestion ? "Run and save" : "Run query"}
							</Button>
						</motion.div>
					)}
					{isExecuting && (
						<motion.div
							key="cta-executing"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={POSTGRES_CARD_SWAP}
							className="flex items-center gap-2 pr-1 text-[11px] text-tertiary"
						>
							<motion.span
								animate={{ opacity: [0.4, 1, 0.4] }}
								transition={{
									duration: 1.4,
									repeat: Infinity,
									ease: "easeInOut",
								}}
								className="inline-block h-1.5 w-1.5 rounded-full bg-current"
							/>
							Running query
						</motion.div>
					)}
					{isExecuted && (
						<motion.div
							key="cta-executed"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={POSTGRES_CARD_SWAP}
							className="flex items-center gap-2 pr-1 text-[11px]"
						>
							<span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
								<Check strokeWidth={2.25} className="h-3.5 w-3.5" />
								Done
							</span>
						</motion.div>
					)}
					{isRejected && (
						<motion.div
							key="cta-rejected"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={POSTGRES_CARD_SWAP}
							className="flex items-center gap-1 pr-1 text-[11px] text-tertiary"
						>
							<XCircle strokeWidth={2} className="h-3.5 w-3.5" />
							<span>Rejected</span>
						</motion.div>
					)}
					{isErrored && (
						<motion.div
							key="cta-errored"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={POSTGRES_CARD_SWAP}
						>
							<Button
								variant="outline"
								size="sm"
								className="h-7 gap-1.5 rounded-md text-xs font-medium"
								disabled={disabled}
								onClick={() => onApprove({ saveWhitelist: false })}
							>
								Try again
							</Button>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</motion.div>
	)
}

export interface AiChatSourceItem {
	source: AICiteItem
	onClick?: (source: AICiteItem) => void
}

export const AiChatSourceList = ({
	sources,
	onSourceClick,
	sourceLabel = "Source",
	sourcesLabel,
}: {
	sources: AICiteItem[]
	onSourceClick?: (source: AICiteItem) => void
	sourceLabel?: string
	sourcesLabel?: string
}) => {
	const [open, setOpen] = useState(false)

	// A source that carries a url lives outside Productlane, so it opens as a
	// real link rather than routing anywhere in the app.
	const renderSource = (source: AICiteItem, onSelected?: () => void) => {
		const className =
			"flex items-center gap-2 text-left text-secondary hover:text-primary"

		return source.url ? (
			<a
				key={source.id}
				href={source.url}
				target="_blank"
				rel="noreferrer"
				onClick={onSelected}
				className={className}
			>
				<span className="truncate">{source.title}</span>
			</a>
		) : (
			<button
				key={source.id}
				onClick={() => {
					onSelected?.()
					onSourceClick?.(source)
				}}
				className={className}
			>
				<span className="truncate">{source.title}</span>
			</button>
		)
	}

	if (sources.length < 2) {
		return (
			<div className="ml-0.5">
				<p className="w-full pb-2 pt-5 text-xxs text-secondary">
					{sourceLabel}
				</p>
				<div className="flex flex-col gap-2 text-xs">
					{sources.map((source) => renderSource(source))}
				</div>
			</div>
		)
	}

	const sourcesContent = (
		<div className="flex max-h-80 flex-col gap-2 overflow-y-auto text-xs">
			{sources.map((source) => renderSource(source, () => setOpen(false)))}
		</div>
	)

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			content={sourcesContent}
			side="top"
			align="start"
			className="w-80 max-w-sm"
		>
			<div className="ml-0.5 w-full cursor-pointer pb-2 pt-5 text-xxs text-secondary transition-opacity hover:opacity-75">
				{sourcesLabel ?? `${sources.length} Sources`}
			</div>
		</Popover>
	)
}

export interface AgentSetupActionArgs {
	messageId: string
	pendingActionId: string
	kind: "linear_issue" | "agent_instructions"
}

export interface AiChatMessageEnrichmentsProps {
	message: AiChatMessage
	index?: number
	isStreaming: boolean
	aiMessages: AiChatMessage[] | null
	showSuggestions?: boolean
	onSendMessage?: (message: string) => void
	onSourceClick?: (source: AICiteItem) => void
	onFeedback?: (input: AiChatFeedbackInput) => void
	onCopyAnswer?: () => void
	onActionClick?: (action: { action: string }) => void
	onContinueInClaude?: () => void
	onApproveStripeAction?: (args: {
		messageId: string
		pendingActionId: string
	}) => void
	onRejectStripeAction?: (args: {
		messageId: string
		pendingActionId: string
	}) => void
	onApprovePostgresAction?: (args: {
		messageId: string
		pendingActionId: string
		saveWhitelist: boolean
	}) => void
	onRejectPostgresAction?: (args: {
		messageId: string
		pendingActionId: string
	}) => void
	onApproveAgentSetupAction?: (args: AgentSetupActionArgs) => void
	onRejectAgentSetupAction?: (args: AgentSetupActionArgs) => void
	showActions?: boolean
	showFeedback?: boolean
	sourceLabel?: string
	sourcesLabel?: string
	isProcessing?: boolean
}

export const AiChatMessageEnrichments = ({
	message,
	isStreaming,
	aiMessages,
	showSuggestions = true,
	onSendMessage,
	onSourceClick,
	onFeedback,
	onCopyAnswer,
	onActionClick,
	onContinueInClaude,
	onApproveStripeAction,
	onRejectStripeAction,
	onApprovePostgresAction,
	onRejectPostgresAction,
	onApproveAgentSetupAction,
	onRejectAgentSetupAction,
	showActions = true,
	showFeedback = true,
	sourceLabel,
	sourcesLabel,
	isProcessing = false,
}: AiChatMessageEnrichmentsProps) => {
	const messageCount = aiMessages?.length ?? 0

	const isCardOutdated = useMemo(() => {
		const identifier =
			message.linearIssueCreated?.identifier ??
			message.linearIssueEdited?.identifier ??
			message.linearIssueLinked?.identifier
		if (!identifier || !aiMessages) return false

		const msgIndex = aiMessages.findIndex((m) => m.id === message.id)
		if (msgIndex === -1) return false

		const laterMessages = aiMessages.slice(msgIndex + 1)
		return laterMessages.some(
			(m) =>
				m.linearIssueEdited?.identifier === identifier ||
				m.linearIssueLinked?.identifier === identifier ||
				m.linearIssueRemoved?.identifier === identifier,
		)
	}, [message, aiMessages])

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	const sendMessage = onSendMessage ?? (() => {})

	return (
		<>
			{!isStreaming && message.linearIssueCreated && (
				<LinearIssueCard
					type="created"
					id={message.linearIssueCreated.id}
					identifier={message.linearIssueCreated.identifier}
					title={message.linearIssueCreated.title}
					description={message.linearIssueCreated.description}
					outdated={isCardOutdated}
					important={message.linearIssueCreated.important}
					messageCount={messageCount}
					sendMessage={sendMessage}
					isProcessing={isProcessing}
				/>
			)}
			{!isStreaming && message.linearIssueLinked && (
				<LinearIssueCard
					type="linked"
					id={message.linearIssueLinked.id}
					identifier={message.linearIssueLinked.identifier}
					title={message.linearIssueLinked.title}
					description={message.linearIssueLinked.description}
					status={message.linearIssueLinked.status}
					outdated={isCardOutdated}
					important={message.linearIssueLinked.important}
					messageCount={messageCount}
					sendMessage={sendMessage}
					isProcessing={isProcessing}
				/>
			)}
			{!isStreaming && message.linearIssueEdited && (
				<LinearIssueCard
					type="edited"
					id={message.linearIssueEdited.id}
					identifier={message.linearIssueEdited.identifier}
					title={message.linearIssueEdited.title}
					description={message.linearIssueEdited.description}
					outdated={isCardOutdated}
					important={message.linearIssueEdited.important}
					messageCount={messageCount}
					sendMessage={sendMessage}
					isProcessing={isProcessing}
				/>
			)}
			{message.linearIssueRemoved && (
				<LinearIssueCard
					type="removed"
					identifier={message.linearIssueRemoved.identifier}
					title={message.linearIssueRemoved.title}
					messageCount={messageCount}
					sendMessage={sendMessage}
					isProcessing={isProcessing}
				/>
			)}

			{!isStreaming && message.stripeActionApproval && (
				<StripeActionApprovalCard
					actionType={message.stripeActionApproval.actionType}
					summary={message.stripeActionApproval.summary}
					preview={message.stripeActionApproval.preview}
					status={message.stripeActionApproval.status}
					subscriptionUrl={message.stripeActionApproval.subscriptionUrl}
					errorMessage={message.stripeActionApproval.errorMessage}
					disabled={isProcessing}
					onApprove={() => {
						if (!onApproveStripeAction || !message.stripeActionApproval) return
						onApproveStripeAction({
							messageId: message.id,
							pendingActionId: message.stripeActionApproval.pendingActionId,
						})
					}}
					onReject={() => {
						if (!onRejectStripeAction || !message.stripeActionApproval) return
						onRejectStripeAction({
							messageId: message.id,
							pendingActionId: message.stripeActionApproval.pendingActionId,
						})
					}}
				/>
			)}

			{!isStreaming && message.postgresActionApproval && (
				<PostgresActionApprovalCard
					sql={message.postgresActionApproval.sql}
					reason={message.postgresActionApproval.reason}
					summary={message.postgresActionApproval.summary}
					status={message.postgresActionApproval.status}
					whitelistSuggestion={
						message.postgresActionApproval.whitelistSuggestion
					}
					result={message.postgresActionApproval.result}
					savedWhitelistName={message.postgresActionApproval.savedWhitelistName}
					errorMessage={message.postgresActionApproval.errorMessage}
					disabled={isProcessing}
					onApprove={({ saveWhitelist }) => {
						if (!onApprovePostgresAction || !message.postgresActionApproval)
							return
						onApprovePostgresAction({
							messageId: message.id,
							pendingActionId: message.postgresActionApproval.pendingActionId,
							saveWhitelist,
						})
					}}
					onReject={() => {
						if (!onRejectPostgresAction || !message.postgresActionApproval)
							return
						onRejectPostgresAction({
							messageId: message.id,
							pendingActionId: message.postgresActionApproval.pendingActionId,
						})
					}}
				/>
			)}

			{!isStreaming && message.linearIssueProposal && (
				<LinearIssueProposalCard
					title={message.linearIssueProposal.title}
					description={message.linearIssueProposal.description}
					teamName={message.linearIssueProposal.teamName}
					status={message.linearIssueProposal.status}
					created={message.linearIssueProposal.created}
					errorMessage={message.linearIssueProposal.errorMessage}
					disabled={isProcessing}
					onCreate={() =>
						onApproveAgentSetupAction?.({
							messageId: message.id,
							pendingActionId: message.linearIssueProposal!.pendingActionId,
							kind: "linear_issue",
						})
					}
					onReject={() =>
						onRejectAgentSetupAction?.({
							messageId: message.id,
							pendingActionId: message.linearIssueProposal!.pendingActionId,
							kind: "linear_issue",
						})
					}
				/>
			)}

			{!isStreaming && message.agentInstructionsProposal && (
				<AgentInstructionsProposalCard
					before={message.agentInstructionsProposal.before}
					after={message.agentInstructionsProposal.after}
					reason={message.agentInstructionsProposal.reason}
					status={message.agentInstructionsProposal.status}
					errorMessage={message.agentInstructionsProposal.errorMessage}
					disabled={isProcessing}
					onSave={() =>
						onApproveAgentSetupAction?.({
							messageId: message.id,
							pendingActionId:
								message.agentInstructionsProposal!.pendingActionId,
							kind: "agent_instructions",
						})
					}
					onReject={() =>
						onRejectAgentSetupAction?.({
							messageId: message.id,
							pendingActionId:
								message.agentInstructionsProposal!.pendingActionId,
							kind: "agent_instructions",
						})
					}
				/>
			)}

			{!isStreaming && message.continueInClaude && (
				<ContinueInClaudeCard
					summary={message.continueInClaude.summary}
					disabled={isProcessing}
					onContinue={() => onContinueInClaude?.()}
				/>
			)}

			{showFeedback &&
				message.processed &&
				message.content &&
				message.id !== "welcome-message" && (
					<AiChatMessageActions
						message={message}
						sentiment={message.feedback?.sentiment ?? null}
						onFeedback={onFeedback}
						onCopy={onCopyAnswer}
					/>
				)}

			{showSuggestions &&
				message.suggestions &&
				(!aiMessages || aiMessages.length === 1) && (
					<div className="flex flex-col items-start gap-2 pb-2 pt-4">
						{message.suggestions.map((suggestion, suggestionIndex) =>
							suggestion ? (
								<AiSuggestionButton
									key={suggestion}
									index={suggestionIndex}
									disabled={isProcessing}
									onClick={() => onSendMessage?.(suggestion)}
								>
									{suggestion}
								</AiSuggestionButton>
							) : null,
						)}
					</div>
				)}

			{showActions && message.actions.length > 0 && onActionClick && (
				<div className="flex flex-col items-center gap-2 pb-2">
					{message.actions.map((action) => (
						<Button
							key={action.action}
							disabled={isProcessing}
							onClick={() => onActionClick(action)}
							variant="outline"
							className="mb-4 mt-4 !w-full max-w-none !bg-transparent py-4 text-sm hover:!bg-gray-100 dark:hover:!bg-gray-900"
						>
							<span className="text-primary">Talk with a human</span>
						</Button>
					))}
				</div>
			)}

			{message.processed &&
				message.usedQueries &&
				message.usedQueries.length > 0 && (
					<UsedQueriesPills queries={message.usedQueries} />
				)}

			{message.processed &&
				message.usedConnectors &&
				message.usedConnectors.length > 0 && (
					<UsedConnectorsPills connectors={message.usedConnectors} />
				)}

			{message.processed && message.sources.length > 0 && (
				<div className="pb-4">
					<AiChatSourceList
						sources={message.sources}
						onSourceClick={onSourceClick}
						sourceLabel={sourceLabel}
						sourcesLabel={sourcesLabel}
					/>
				</div>
			)}
		</>
	)
}

const UsedConnectorsPills = ({
	connectors,
}: {
	connectors: ConnectorAttribution[]
}) => {
	const reduce = useReducedMotion()
	return (
		<motion.div
			initial={reduce ? false : { opacity: 0, y: 4 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
			className="mt-2 flex flex-wrap items-center gap-1.5 pb-2"
		>
			<span className="flex items-center gap-1 text-[10px] font-medium tracking-[0.08em] text-tertiary">
				<SquaresConnected strokeWidth={1.75} className="h-3.5 w-3.5" />
				via
			</span>
			{connectors.map((connector) => (
				<Badge
					key={`${connector.sectionId}-${connector.tool}`}
					variant={connector.ok ? "default" : "red"}
					size="sm"
					className="max-w-[12rem] cursor-default"
					title={
						connector.ok
							? `${connector.title}: ${connector.tool}`
							: `${connector.title}: ${connector.tool} failed`
					}
				>
					<span className="truncate">{connector.title}</span>
					<span className="truncate font-mono text-tertiary">
						{connector.tool}
					</span>
				</Badge>
			))}
		</motion.div>
	)
}

const UsedQueriesPills = ({ queries }: { queries: { name: string }[] }) => {
	const reduce = useReducedMotion()
	return (
		<motion.div
			initial={reduce ? false : { opacity: 0, y: 4 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
			className="mt-2 flex flex-wrap items-center gap-1.5 pb-2"
		>
			<span className="flex items-center gap-1 text-[10px] font-medium tracking-[0.08em] text-tertiary">
				<Database strokeWidth={1.75} className="h-3.5 w-3.5" />
				via
			</span>
			{queries.map((q) => (
				<span
					key={q.name}
					className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10.5px] tabular-nums text-secondary dark:bg-gray-800/60"
					title={`Whitelisted query: ${q.name}`}
				>
					{q.name}
				</span>
			))}
		</motion.div>
	)
}
